import React, { useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { MarkdownToolbar } from './MarkdownToolbar';

export interface HeroValues {
  headline: string;
  intro: string;
}

interface Props {
  values: HeroValues;
  onChange: (patch: Partial<HeroValues>) => void;
}

const inputCls = 'ds-input';

const Tab: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({
  active, onClick, children,
}) => (
  <button
    type="button"
    onClick={onClick}
    className="ds-btn ds-btn-ghost border-b-2 rounded-none min-h-[44px] px-4 touch-manipulation"
    style={
      active
        ? { borderColor: 'var(--ds-brand)', color: 'var(--ds-brand)' }
        : { borderColor: 'transparent' }
    }
  >
    {children}
  </button>
);

export const HeroEditor: React.FC<Props> = ({ values, onChange }) => {
  const [tab, setTab] = useState<'edit' | 'preview'>('edit');
  const introRef = useRef<HTMLTextAreaElement>(null);

  return (
    <div className="flex flex-col gap-4">
      {/* Headline */}
      <div className="flex flex-col gap-1">
        <label className="t-label">
          Hero Headline
        </label>
        <input
          value={values.headline}
          onChange={(e) => onChange({ headline: e.target.value })}
          placeholder="Land your dream job in 30 days"
          maxLength={200}
          className={inputCls}
        />
      </div>

      {/* Intro with markdown toolbar + preview */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between">
          <label className="t-label">
            Hero Intro
          </label>
          <div className="flex">
            <Tab active={tab === 'edit'} onClick={() => setTab('edit')}>Edit</Tab>
            <Tab active={tab === 'preview'} onClick={() => setTab('preview')}>Preview</Tab>
          </div>
        </div>

        {tab === 'edit' ? (
          <>
            <MarkdownToolbar
              textareaRef={introRef}
              value={values.intro}
              onChange={(v) => onChange({ intro: v })}
            />
            <textarea
              ref={introRef}
              value={values.intro}
              onChange={(e) => onChange({ intro: e.target.value })}
              rows={4}
              placeholder="Write a compelling intro — use the toolbar to add links…"
              className="ds-textarea rounded-t-none font-mono"
            />
          </>
        ) : (
          <div className="ds-card-sm min-h-[6rem] prose prose-sm max-w-none dark:prose-invert">
            {values.intro ? (
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{values.intro}</ReactMarkdown>
            ) : (
              <span className="t-meta italic">Nothing to preview yet.</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
